import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { NewAuthService } from './new-auth-service.service';
import { TokenService } from './token.service';

// The login call itself legitimately answers 401 on a wrong password. Tearing the
// session down there is pointless (there is no session yet) and it is the most
// travelled 401 path now that transient failures are retried instead of reloading.
// Telemetry must never end a session: a denial report sent with a stale token would otherwise sign the user out.
const AUTH_ENDPOINTS_EXEMPT_FROM_SIGN_OUT = ['/api/v1/auth/authorize', '/api/v1/auth/refresh-access-token', '/api/v1/client-telemetry/permission-denied'];

/** welbyEndpoint without a trailing slash, read per request so a preview's `?backend=` override always applies. */
const welbyApiOrigin = (): string => (environment.welbyEndpoint ?? '').replace(/\/$/, '');

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Guards against firing signOut() repeatedly when a burst of concurrent requests all 401 at once.
  private static isSigningOut = false;

  constructor(
    private authService: NewAuthService,
    private tokenService: TokenService
  ) {
    // Release the guard when a NEW session is established, not when signOut() settles.
    // A sign-out is one round trip, but the 401s it targets arrive over seconds, so
    // resetting on settle let every later 401 re-run the full teardown
    // (disconnectDevice + signOut + navigate). Releasing on login keeps the second
    // session in the same tab able to sign out on a 401, which is why the guard must
    // not simply latch forever.
    this.authService.isLoggedObservable$.subscribe((isLogged) => {
      if (isLogged) {
        AuthInterceptor.isSigningOut = false;
      }
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Scoped to welby-micro-service, which is what issued this token and what owns the session.
    // Both branches below hang off this: a request to another backend neither carries the Welby
    // JWT nor can end the session with its own 401. See `isWelbyApiUrl`.
    const isWelbyApi = this.isWelbyApiUrl(req.url);
    let newReq = req.clone();

    if (isWelbyApi) {
      if (!this.tokenService.tokenIsExpired()) {
        newReq = req.clone({
          headers: req.headers.set('Authorization', 'Bearer ' + this.tokenService.getToken()),
        });
      }
    }

    // Response
    return next.handle(newReq).pipe(
      catchError((error) => {
        // Catch "401 Unauthorized" responses
        if (error instanceof HttpErrorResponse && error.status === 401 && isWelbyApi && !this.isExemptFromSignOut(newReq.url)) {
          if (!AuthInterceptor.isSigningOut) {
            AuthInterceptor.isSigningOut = true;
            // signOut() already routes to /login via the Angular router (no full page reload),
            // so a degraded backend returning 401s no longer triggers a synchronized reload storm
            // across every open tab. The guard is released on the next successful login (see
            // the constructor), not when this promise settles.
            Promise.resolve(this.authService.signOut()).catch(() => undefined);
          }
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Whether this request goes to the API that issued the session token.
   *
   * The Welby JWT is signed by welby-micro-service with its own secret; every other backend the
   * app talks to (markus-call-processor, reached via `call_transcriptions_endpoint` and
   * `markusApiUrl`) authenticates Firebase ID tokens instead and answers 401 to this one. Matching
   * on `/api/v1` alone made any such 401 indistinguishable from an expired session: adding
   * `AuthGuard` to two previously open call-processor routes signed care managers out mid-call,
   * because the inbound-call dialog looks the caller up there the moment the call is answered.
   *
   * The `/api/v1` part of the check is kept so nothing outside the versioned API — the reports
   * socket, for instance — starts receiving the header it never had.
   */
  private isWelbyApiUrl(url: string): boolean {
    const origin = welbyApiOrigin();
    return !!origin && url.startsWith(origin) && url.indexOf('/api/v1') > -1;
  }

  private isExemptFromSignOut(url: string): boolean {
    return AUTH_ENDPOINTS_EXEMPT_FROM_SIGN_OUT.some((endpoint) => url.indexOf(endpoint) > -1);
  }
}
